// Dashboard API.
//
// Wire shapes derive from the backend OpenAPI codegen
// (`components["schemas"]["DashboardSummaryResponse"]` and friends). View
// types narrow the codegen output's optional scalars to non-optional for
// the UI; the dashboard endpoint always populates every field in practice.

import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import type { components } from "@/types/api";
import type { ListingType, PropertyStatus } from "@/types/property";
import type { LeadStatus } from "@/features/lead/types";
import type { ActivityAction } from "@/features/dashboard/activity-action-labels";

// ---------------------------------------------------------------------------
// Wire types (1:1 with `components.schemas`)
// ---------------------------------------------------------------------------

export type DashboardSummaryWire = components["schemas"]["DashboardSummaryResponse"];
export type RecentActivityWire = components["schemas"]["RecentActivity"];
export type AgentActivityWire = components["schemas"]["AgentActivity"];

// ---------------------------------------------------------------------------
// View-model: narrow the codegen output's all-optional scalars to the
// always-present shape the page renders. Replaces the previously
// hand-rolled DashboardSummary / DashboardActivityItem / DashboardAgentActivityItem.
// ---------------------------------------------------------------------------

export interface DashboardActivityItem {
  id: number;
  entityType: string;
  entityId: number;
  action: ActivityAction | string;
  actorFullName: string;
  createdAt: string;
  // Backend may omit the field entirely, send null, or send a non-empty
  // string. All three need to be tolerated at the render site.
  detail: string | null | undefined;
}

export interface DashboardAgentActivityItem {
  userId: number;
  fullName: string;
  createdCount: number;
  updatedCount: number;
  soldCount: number;
}

export interface DashboardSummary {
  properties: {
    total: number;
    byStatus: Record<PropertyStatus, number>;
    byListingType: Record<ListingType, number>;
    staleCount: number;
  };
  leads: {
    total: number;
    byStatus: Record<LeadStatus, number>;
  };
  appointments: {
    thisWeek: number;
    upcoming7d: number;
    completedThisMonth: number;
  };
  recentActivity: DashboardActivityItem[];
  agentActivity: DashboardAgentActivityItem[];
  aiUsage: {
    promptTokensThisMonth: number;
    completionTokensThisMonth: number;
    monthlyQuota: number;
  };
}

export const dashboardKeys = {
  all: ["dashboard"] as const,
  summary: () => [...dashboardKeys.all, "summary"] as const,
};

function fetchDashboardSummary(): Promise<DashboardSummary> {
  return apiRequest<DashboardSummary>("/dashboard/summary");
}

export function useDashboardSummary() {
  return useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: fetchDashboardSummary,
    // Owners hit the dashboard once a session; refresh on tab focus
    // is enough — no need for aggressive polling.
    staleTime: 60_000,
  });
}
